import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, RotateCw, ArrowUpDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import EmptyState from '../components/EmptyState';
import BuyerNameReviewCard from '../components/BuyerNameReviewCard';
import IdentityReviewCard from '../components/IdentityReviewCard';
// Fix 34 (2026-09-12): importar el export de TikTok para cargar datos en lote.
import TikTokExportImportDialog from '../components/TikTokExportImportDialog';
// Fix 36 (2026-09-17): switch de autocompletado de compradores ya conocidos.
import KnownBuyerAutoFillSwitch from '../components/KnownBuyerAutoFillSwitch';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';

export default function ManualReviewPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('pending');
  const [sort, setSort] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [notes, setNotes] = useState({});

  const q = useQuery({
    queryKey: ['manual-review', { type, status, sort, page }],
    queryFn: async () => {
      const params = { status, sort, page, page_size: pageSize };
      if (type && type !== 'all') params.type = type;
      return (await api.get('/manual-review', { params })).data;
    },
    // Fix 33 (2026-09-12): los casos se cierran solos cuando TikTok libera los
    // datos; la pantalla no puede quedar mostrando uno viejo para aprobar.
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const dry = (await api.post('/admin/cleanup-obsolete-manual-review', null,
        { params: { dry_run: true } })).data;
      if (!dry.matched) return { matched: 0, archived: 0 };
      if (!window.confirm(
        t('manual_review.cleanup_confirm').replace('{{count}}', dry.matched)
      )) {
        return { skipped: true };
      }
      return (await api.post('/admin/cleanup-obsolete-manual-review', null,
        { params: { dry_run: false } })).data;
    },
    onSuccess: (data) => {
      if (data.skipped) return;
      if (!data.matched) {
        toast.success(t('manual_review.cleanup_none'));
        return;
      }
      toast.success(t('manual_review.cleanup_done').replace('{{count}}', data.archived));
      qc.invalidateQueries({ queryKey: ['manual-review'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => toast.error(t('manual_review.cleanup_error')),
  });

  const actionMutation = useMutation({
    mutationFn: async ({ itemId, action, item }) =>
      (await api.post(`/manual-review/${itemId}/action`, {
        action,
        notes: notes[itemId] || '',
      })).data,
    onSuccess: () => {
      toast.success('Acción registrada');
      qc.invalidateQueries({ queryKey: ['manual-review'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => toast.error('Error registrando la acción'),
  });

  const retryMutation = useMutation({
    mutationFn: async (tiktokOrderId) =>
      (await api.post(`/manual-review/retry/${encodeURIComponent(tiktokOrderId)}`)).data,
    onSuccess: (data) => {
      const act = data?.result?.action || 'processed';
      toast.success(`${t('manual_review.retry_sync_success')} — ${act}`);
      qc.invalidateQueries({ queryKey: ['manual-review'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || '';
      toast.error(`${t('manual_review.retry_sync_error')}${detail ? `: ${detail}` : ''}`);
    },
  });

  const items = q.data?.items || [];
  const total = q.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="manual-review-page">
      <PageHeader
        title={t('manual_review.title')}
        subtitle={t('manual_review.subtitle')}
        actions={
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => cleanupMutation.mutate()}
                disabled={cleanupMutation.isPending}
                data-testid="manual-review-cleanup-button"
              >
                {cleanupMutation.isPending
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Trash2 className="h-4 w-4 mr-2" />}
                {t('manual_review.cleanup_button')}
              </Button>
            )}
            <KnownBuyerAutoFillSwitch />
            <TikTokExportImportDialog />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ['manual-review'] })}
              data-testid="manual-review-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> {t('common.refresh')}
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t('manual_review.filter_status')}</Label>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="manual-review-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">{t('manual_review.status_pending')}</SelectItem>
            <SelectItem value="archived">{t('manual_review.status_archived')}</SelectItem>
            <SelectItem value="resolved">{t('manual_review.status_resolved')}</SelectItem>
            <SelectItem value="all">{t('manual_review.status_all')}</SelectItem>
          </SelectContent>
        </Select>

        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t('common.type')}</Label>
        <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
          <SelectTrigger className="w-72" data-testid="manual-review-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.filter')} —</SelectItem>
            <SelectItem value="buyer_name_review">{t('manual_review.type_buyer_name_review')}</SelectItem>
            <SelectItem value="identity_review">{t('manual_review.type_identity_review')}</SelectItem>
            <SelectItem value="order_sync_failed">{t('manual_review.type_order_sync_failed')}</SelectItem>
            <SelectItem value="ambiguous_match">{t('manual_review.type_ambiguous_match')}</SelectItem>
            <SelectItem value="name_zip_match_diff_street">{t('manual_review.type_name_zip_match_diff_street')}</SelectItem>
            <SelectItem value="missing_invoice">{t('manual_review.type_missing_invoice')}</SelectItem>
            <SelectItem value="unmapped_sku">{t('manual_review.type_unmapped_sku')}</SelectItem>
            <SelectItem value="uncategorized_charge">{t('manual_review.type_uncategorized_charge')}</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setSort((s) => (s === 'desc' ? 'asc' : 'desc')); setPage(1); }}
          data-testid="manual-review-sort-toggle"
          title={sort === 'desc' ? t('manual_review.sort_newest') : t('manual_review.sort_oldest')}
        >
          <ArrowUpDown className="h-4 w-4 mr-2" />
          {sort === 'desc' ? t('manual_review.sort_newest') : t('manual_review.sort_oldest')}
        </Button>

        <div className="ml-auto text-[12px] text-muted-foreground">
          <span className="font-semibold text-foreground/80">{total}</span> {t('manual_review.title').toLowerCase()}
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <EmptyState message={t('manual_review.no_pending')} testid="manual-review-empty" />
      ) : (
        <div className="space-y-3" data-testid="manual-review-queue">
          {items.map((it) => {
            const itemId = it.operation_key || `${it.created_at}-${it.type}`;
            const isPending = it.status === 'pending';
            if (it.type === 'buyer_name_review') {
              return (
                <BuyerNameReviewCard
                  key={itemId}
                  item={it}
                  itemId={itemId}
                  locale={locale}
                />
              );
            }
            if (it.type === 'identity_review') {
              return (
                <IdentityReviewCard
                  key={itemId}
                  item={it}
                  itemId={itemId}
                  locale={locale}
                />
              );
            }
            return (
              <Card
                key={itemId}
                className="bg-card border-border border-l-4 border-l-[hsl(var(--accent))]"
                data-testid={`manual-review-item-${it.type}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[hsl(var(--accent))]/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--accent))]">
                          {t(`manual_review.type_${it.type}`)}
                        </span>
                        {!isPending && (
                          <span
                            className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                            data-testid={`manual-review-item-status-${itemId}`}
                          >
                            {t(`manual_review.status_${it.status}`) || it.status}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{formatDate(it.created_at, locale)}</span>
                      </div>
                      <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-muted p-2 text-[11px] font-mono leading-snug">
{JSON.stringify(it.payload || it.candidates || it, null, 2)}
                      </pre>
                    </div>
                    {isPending && (
                      <div className="flex w-full lg:w-72 flex-col gap-2">
                        {it.type === 'order_sync_failed' && it.tiktok_order_id && (
                          <Button
                            size="sm"
                            variant="default"
                            disabled={retryMutation.isPending}
                            onClick={() => retryMutation.mutate(it.tiktok_order_id)}
                            data-testid={`manual-review-retry-${it.tiktok_order_id}`}
                            className="w-full"
                          >
                            {retryMutation.isPending && retryMutation.variables === it.tiktok_order_id
                              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              : <RotateCw className="h-4 w-4 mr-2" />}
                            {t('manual_review.retry_sync')}
                          </Button>
                        )}
                        <Input
                          placeholder={t('common.notes')}
                          value={notes[itemId] || ''}
                          onChange={(e) => setNotes((n) => ({ ...n, [itemId]: e.target.value }))}
                          data-testid={`manual-review-notes-${it.type}`}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            onClick={() => actionMutation.mutate({ itemId, action: 'approve', item: it })}
                            data-testid="manual-review-approve-button"
                          >
                            {t('common.approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => actionMutation.mutate({ itemId, action: 'reject', item: it })}
                            data-testid="manual-review-reject-button"
                          >
                            {t('common.reject')}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => actionMutation.mutate({ itemId, action: 'merge', item: it })}
                            data-testid="manual-review-merge-button"
                          >
                            {t('common.merge')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => actionMutation.mutate({ itemId, action: 'forward', item: it })}
                            data-testid="manual-review-forward-button"
                          >
                            {t('common.forward')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-[12px] text-muted-foreground">
            {t('common.page_of').replace('{{page}}', page).replace('{{pages}}', totalPages)}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              data-testid="manual-review-pagination-prev"
            >
              {t('common.pagination_prev')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="manual-review-pagination-next"
            >
              {t('common.pagination_next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
